import { useMemo, useState } from "react";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import {
  useGetBusinessStats,
  useGetBusinessWeeklyStats,
} from "@/business/query";
import { useBusiness } from "@/core/business";
import { NoReferrals } from "@/referrals/no-referrals";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";
import { DashboardSectionHeader } from "./dashboard-section-header";
import { DashboardSidebar } from "./dashboard-sidebar";
import ParticipationTrackerPane from "./participation-tracker-pane/participation-tracker-pane";
import { PERIOD_WEEKS, PerformanceStats } from "./performance-stats";
import ReferralsChartPane from "./referrals-chart-pane";
import SignupsChartPane from "./signups-chart-pane";
import TextMessagesPane from "./text-messages-pane";

const Dashboard = () => {
  const { businessId } = useBusiness();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<IMostRecentTextMessage | null>(null);

  const { data: businessStats } = useGetBusinessStats(businessId);
  const {
    data: weeklyStats,
    isLoading,
    isError,
  } = useGetBusinessWeeklyStats(businessId);

  const hasNoActivity = useMemo(
    () =>
      !weeklyStats?.some((stat) => stat.referral_count > 0) &&
      !weeklyStats?.some((stat) => stat.total_signup_count > 0),
    [weeklyStats, businessStats],
  );

  // A failed load isn't "no activity": the panes below show what failed.
  if (hasNoActivity && !isLoading && !isError) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" />
        <NoReferrals />
      </PageContainer>
    );
  }

  return (
    <SimpleSidebarLayout
      open={isSidebarOpen}
      onOpenChange={(open) => {
        setSelectedItem(open ? selectedItem : null);
        setIsSidebarOpen(open);
      }}
      sidebarWidth="436px"
    >
      <SimpleSidebarContent className="h-full" removeScrollArea>
        <div className="h-full w-full overflow-y-auto">
          <PageContainer>
            <PageHeader title="Dashboard" />
            {/* The open text-message panel takes 436px from the page, so the
                page drops to its narrow layout until the panel closes. */}
            <Stack className="w-full" align="start" gap={2}>
              <DashboardSectionHeader
                title="Performance"
                caption={`Last ${PERIOD_WEEKS} full weeks`}
                link={{ to: "/referrals", label: "View referrals" }}
              />
              <PerformanceStats isCompact={isSidebarOpen} />
            </Stack>
            <Stack
              gap={4}
              align="stretch"
              className={cn("w-full", cn({ "lg:flex-row": !isSidebarOpen }))}
            >
              <SignupsChartPane />
              <ReferralsChartPane />
            </Stack>
            <Stack
              gap={4}
              align="stretch"
              className={cn(
                "w-full",
                cn({ "lg:flex-row lg:items-start": !isSidebarOpen }),
              )}
            >
              <TextMessagesPane
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                setIsSidebarOpen={setIsSidebarOpen}
              />
              <ParticipationTrackerPane
                className={cn({ "lg:w-96 lg:shrink-0": !isSidebarOpen })}
              />
            </Stack>
          </PageContainer>
        </div>
      </SimpleSidebarContent>
      <DashboardSidebar
        isOpen={isSidebarOpen}
        selectedItem={selectedItem}
        setIsSidebarOpen={setIsSidebarOpen}
      />
    </SimpleSidebarLayout>
  );
};

export default Dashboard;
